import React from "react";

/**
 * LinkCityScene — cinematic "Link City" backdrop for /welcome, /login,
 * /onboarding, and /verify-email.
 *
 * Iter 68: switched from a CSS-only synthetic skyline to a real
 * photographic asset (link-city-night.jpg — a dusk skyline + curved
 * highway + bridge over water at golden hour fading into deep purple
 * twilight). The photo gives the entry surfaces a real-place,
 * commute-grade quality that pure CSS couldn't match.
 *
 * We still own the mood: photographic layer is desaturated slightly and
 * cooled toward Link navy, then layered with brand-orange + purple
 * gradients, vignette, and grain. The result reads as Link's universe,
 * not stock imagery.
 *
 * Layers, back-to-front:
 *   1. Photographic backdrop with Ken Burns slow drift + scale.
 *   2. Navy color overlay (multiply) to push the photo into Link palette.
 *   3. Warm orange bloom radiating from below-center (where the CTA
 *      cluster sits) — keeps focus where action lives.
 *   4. Purple haze on the upper-right to balance the warm bloom.
 *   5. Vignette + grain for filmic finish.
 *
 * Motion respects prefers-reduced-motion via both the CSS media query
 * and the in-app html[data-reduced-motion="1"] attribute we manage from
 * /preferences/accessibility.
 */
export default function LinkCityScene() {
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      data-testid="welcome-scene"
      aria-hidden="true"
    >
      {/* 1. Photographic backdrop — slow Ken Burns drift+scale so the
             city subtly breathes during dwell time on the splash.
             Iter 69: aggressive desaturation + hue rotation to push the
             photo's heavy purple twilight toward Link navy. The warm
             orange streetlights/bridge reflections survive because
             they're orange-hued not purple-hued.  We still keep a touch
             of saturation so the city doesn't go fully monochrome. */}
      <div
        className="absolute inset-0 link-city-kenburns"
        style={{
          backgroundImage: "url('/assets/link-city-night.jpg?v=2')",
          backgroundSize: "cover",
          backgroundPosition: "65% 55%",
          backgroundRepeat: "no-repeat",
          filter:
            "hue-rotate(-55deg) saturate(0.55) contrast(1.10) brightness(0.86)",
          willChange: "transform",
        }}
      />

      {/* 2a. Navy COLOR-blend overlay — `mix-blend-mode: color`
             replaces the hue of the underlying pixels with our brand
             navy while leaving the luminance (detail, highlights,
             shadows) intact. This is the heavy hitter that neutralises
             whatever purple survives the filter on layer 1. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: "#0A1F4A",
          mixBlendMode: "color",
          opacity: 0.65,
        }}
      />

      {/* 2b. Navy darkness wash — pushes overall exposure toward Link's
             deep-navy nighttime feel after the color blend has
             neutralised the purple. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,17,40,0.42) 0%, rgba(10,17,40,0.22) 45%, rgba(10,17,40,0.46) 100%)",
          mixBlendMode: "multiply",
        }}
      />

      {/* 3. Warm orange bloom from the lower-center — re-injected after
             the color-blend layer so the orange CTA-warmth still feels
             alive. Sits ABOVE the navy color blend so it isn't
             desaturated by it. */}
      <div
        className="absolute inset-0 link-bloom-pulse"
        style={{
          background: `
            radial-gradient(ellipse 100% 70% at 50% 115%,
              rgba(237, 101, 0, 0.48) 0%,
              rgba(237, 101, 0, 0.22) 22%,
              rgba(237, 101, 0, 0.0) 55%)
          `,
        }}
      />

      {/* 4. Cool navy accent in the upper-right — was violet, now
             pure Link-navy so the corner reads as "deep sky". */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 70% 55% at 80% 18%,
              rgba(20, 35, 75, 0.55) 0%,
              rgba(20, 35, 75, 0.0) 60%)
          `,
        }}
      />

      {/* 5. Vignette + filmic grain. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 120% 90% at 50% 50%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)",
        }}
      />
      <div
        className="absolute inset-0 mix-blend-overlay opacity-[0.10]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: "160px 160px",
        }}
      />

      <style>{`
        @keyframes link-city-kenburns {
          0%   { transform: scale(1.06) translate3d(0, 0, 0); }
          50%  { transform: scale(1.10) translate3d(-1.2%, -0.8%, 0); }
          100% { transform: scale(1.06) translate3d(0, 0, 0); }
        }
        @keyframes link-bloom-pulse {
          0%, 100% { opacity: 0.88; }
          50%      { opacity: 1; }
        }
        .link-city-kenburns { animation: link-city-kenburns 30s ease-in-out infinite; }
        .link-bloom-pulse   { animation: link-bloom-pulse 7s ease-in-out infinite; will-change: opacity; }
        @media (prefers-reduced-motion: reduce) {
          .link-city-kenburns, .link-bloom-pulse {
            animation: none !important;
            transform: scale(1.06);
          }
        }
        html[data-reduced-motion="1"] .link-city-kenburns,
        html[data-reduced-motion="1"] .link-bloom-pulse {
          animation: none !important;
        }
        html[data-reduced-motion="1"] .link-city-kenburns {
          transform: scale(1.06);
        }
      `}</style>
    </div>
  );
}
